import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Mail,
  MessageSquare,
  ShoppingBag,
  Download,
  LogOut,
  Loader2,
  ExternalLink,
  Euro,
  Users,
  RefreshCw,
  FileDown,
  Send,
  CalendarClock,
  Sparkles,
  Search,
  Plus,
  Trash2,
  Reply,
  Megaphone,
  KeyRound,
  X,
  BookOpen,
  Target,
  BarChart3,
  BookMarked,
  Calculator,
} from "lucide-react";
import { adminGet, adminMe, adminPost, clearToken, formatApiError, getToken } from "@/lib/adminApi";
import axios from "axios";
import AdminCharts from "@/components/admin/AdminCharts";
import AdminModal from "@/components/admin/AdminModal";
import MembersTab from "@/components/admin/MembersTab";
import CoursesTab from "@/components/admin/CoursesTab";
import OutreachTab from "@/components/admin/OutreachTab";
import LeadsTab from "@/components/admin/LeadsTab";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import BookstoresTab from "@/components/admin/BookstoresTab";
import LedgerTab from "@/components/admin/LedgerTab";
import PremiumCoursesTab from "@/components/admin/PremiumCoursesTab";
import ChatWidget from "@/components/chat/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHdr() {
  return { Authorization: `Bearer ${getToken()}` };
}

const TABS = [
  { id: "subscribers", label: "Iscritti", icon: Mail, endpoint: "/admin/subscribers", exportPath: "/admin/export/subscribers.csv" },
  { id: "leads", label: "Lead CRM", icon: Target, endpoint: null, exportPath: null },
  { id: "analytics", label: "Analytics", icon: BarChart3, endpoint: null, exportPath: null },
  { id: "bookstores", label: "Librerie", icon: BookMarked, endpoint: null, exportPath: null },
  { id: "ledger", label: "Contabilità", icon: Calculator, endpoint: null, exportPath: null },
  { id: "premium", label: "Corsi Premium", icon: Sparkles, endpoint: null, exportPath: null },
  { id: "members", label: "Membri Area", icon: Users, endpoint: null, exportPath: null },
  { id: "courses", label: "Corsi", icon: BookOpen, endpoint: null, exportPath: null },
  { id: "outreach", label: "Outreach", icon: Send, endpoint: null, exportPath: null },
  { id: "messages", label: "Messaggi", icon: MessageSquare, endpoint: "/admin/messages", exportPath: "/admin/export/messages.csv" },
  { id: "transactions", label: "Transazioni", icon: ShoppingBag, endpoint: "/admin/transactions", exportPath: "/admin/export/transactions.csv" },
  { id: "downloads", label: "Download PDF", icon: Download, endpoint: "/admin/downloads", exportPath: null },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("subscribers");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [chartDays, setChartDays] = useState(30);
  const [chartData, setChartData] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [digestInfo, setDigestInfo] = useState(null);
  const [digestNext, setDigestNext] = useState(null);
  const [dripStatus, setDripStatus] = useState(null);
  const [dripRunning, setDripRunning] = useState(false);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [modal, setModal] = useState(null); // { type: 'add'|'broadcast'|'reply'|'pw', payload? }

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const u = await adminMe();
        setMe(u);
        const s = await adminGet("/admin/stats");
        setStats(s);
      } catch (err) {
        clearToken();
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, me]);

  // Debounced search reload
  useEffect(() => {
    if (!me) return;
    const t = setTimeout(() => loadTab(tab), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const c = await adminGet(`/admin/charts?days=${chartDays}`);
        setChartData(c);
      } catch {
        setChartData(null);
      }
    })();
  }, [chartDays, me]);

  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const r = await adminGet("/admin/digest/next-run");
        setDigestNext(r?.next_run || null);
      } catch {
        setDigestNext(null);
      }
      try {
        const d = await adminGet("/admin/drip/status");
        setDripStatus(d);
      } catch {
        setDripStatus(null);
      }
      try {
        const ea = await adminGet("/admin/email/analytics?days=30");
        setEmailAnalytics(ea);
      } catch {
        setEmailAnalytics(null);
      }
    })();
  }, [me]);

  const runDripNow = async () => {
    setDripRunning(true);
    setError("");
    try {
      await adminPost("/admin/drip/run-now");
      const d = await adminGet("/admin/drip/status");
      setDripStatus(d);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDripRunning(false);
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm("Eliminare questo iscritto?")) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: authHdr() });
      loadTab(tab);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    setDigestInfo(null);
    setError("");
    try {
      const r = await adminPost("/admin/digest/send-now");
      setDigestInfo(r);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDigestSending(false);
    }
  };

  const loadTab = async (id) => {
    const def = TABS.find((t) => t.id === id);
    if (!def?.endpoint) {
      setLoading(false);
      setData(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const qs = searchQ ? `?q=${encodeURIComponent(searchQ)}` : "";
      const res = await adminGet(def.endpoint + qs);
      setData(res);
    } catch (err) {
      setError(formatApiError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const s = await adminGet("/admin/stats");
      setStats(s);
    } catch {
      // non-fatal — keep previous stats on screen
    }
    loadTab(tab);
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const exportCsv = async () => {
    const def = TABS.find((t) => t.id === tab);
    if (!def?.exportPath) return;
    try {
      const res = await fetch(`${API}${def.exportPath}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const dispo = res.headers.get("Content-Disposition") || "";
      const m = dispo.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `${def.id}.csv`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const syncAudience = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError("");
    try {
      const r = await adminPost("/admin/sync-audience");
      setSyncResult(r);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  if (!me) {
    return (
      <main className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0B192C]" size={28} />
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[#F9F8F6] text-[#0B192C]"
    >
      {/* Top bar */}
      <header className="border-b border-[#E5E1DA] bg-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="font-serif-display text-xl tracking-tight"
              data-testid="admin-brand"
            >
              Metodo<span className="text-[#E89B6C]">·</span>di
              <span className="text-[#E89B6C]">·</span>Mare
            </Link>
            <span className="overline hidden sm:inline-block px-2 py-0.5 border border-[#0B192C] text-[10px]">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">
              {me.email}
            </span>
            <button
              onClick={() => setModal({ type: "broadcast" })}
              data-testid="admin-open-broadcast-btn"
              title="Componi un'email broadcast"
              className="inline-flex items-center gap-2 px-3 py-2 bg-[#0E7C8F] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] transition-colors"
            >
              <Megaphone size={13} />
              <span className="hidden md:inline">Broadcast</span>
            </button>
            <button
              onClick={() => setModal({ type: "pw" })}
              data-testid="admin-open-pw-btn"
              title="Cambia password"
              className="w-10 h-10 flex items-center justify-center border border-[#0B192C]/20 hover:border-[#0B192C]/60 transition-colors"
            >
              <KeyRound size={14} />
            </button>
            <button
              onClick={refresh}
              data-testid="admin-refresh-btn"
              title="Aggiorna"
              className="w-10 h-10 flex items-center justify-center border border-[#0B192C]/20 hover:border-[#0B192C]/60 transition-colors"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
            >
              <LogOut size={13} />
              Esci
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="border-b border-[#E5E1DA] bg-[#F4E8D6]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
          <StatCard
            icon={Mail}
            label="Iscritti"
            value={stats?.subscribers ?? "—"}
            testid="stat-subscribers"
          />
          <StatCard
            icon={MessageSquare}
            label="Messaggi"
            value={stats?.messages ?? "—"}
            testid="stat-messages"
          />
          <StatCard
            icon={ShoppingBag}
            label="Transazioni"
            value={stats?.transactions ?? "—"}
            sublabel={
              stats ? `${stats.paid_transactions} pagate` : ""
            }
            testid="stat-transactions"
          />
          <StatCard
            icon={Euro}
            label="Ricavi"
            value={
              stats
                ? `€ ${stats.revenue_eur.toFixed(2).replace(".", ",")}`
                : "—"
            }
            testid="stat-revenue"
          />
          <StatCard
            icon={Download}
            label="Download PDF"
            value={stats?.lead_downloads ?? "—"}
            testid="stat-downloads"
          />
        </div>
      </section>

      {/* Charts */}
      {chartData && (
        <AdminCharts
          data={chartData}
          days={chartDays}
          onDaysChange={setChartDays}
        />
      )}

      {/* Weekly digest panel */}
      <section className="border-b border-[#E5E1DA] bg-[#F4E8D6]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <CalendarClock
              size={20}
              strokeWidth={1.4}
              className="text-[#E89B6C] mt-0.5"
            />
            <div>
              <p className="overline">Report settimanale automatico</p>
              <p className="text-sm font-light text-[#0B192C] mt-1">
                Ogni lunedì alle <strong>09:00</strong> ricevi su{" "}
                <code className="font-mono text-xs">{me.email}</code> un
                riepilogo della settimana.
                {digestNext && (
                  <>
                    {" "}
                    Prossimo invio:{" "}
                    <strong>
                      {new Date(digestNext).toLocaleString("it-IT", {
                        weekday: "long",
                        day: "2-digit",
                        month: "long",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </strong>
                    .
                  </>
                )}
              </p>
              {digestInfo && (
                <p
                  data-testid="digest-sent-msg"
                  className="text-xs text-[#0E7C8F] mt-2"
                >
                  ✓ Inviato ora · id{" "}
                  <code className="font-mono">
                    {digestInfo.message_id?.slice(0, 8)}...
                  </code>
                </p>
              )}
            </div>
          </div>
          <button
            onClick={sendDigestNow}
            disabled={digestSending}
            data-testid="admin-digest-send-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {digestSending ? (
              <>
                <Loader2 size={13} className="animate-spin" /> Invio...
              </>
            ) : (
              <>
                <Send size={13} /> Inviami il report ora
              </>
            )}
          </button>
        </div>
      </section>

      {/* Drip funnel panel */}
      {dripStatus && (
        <section
          data-testid="admin-drip-panel"
          className="border-b border-[#E5E1DA] bg-[#D6E5EC]/40"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Sparkles
                  size={20}
                  strokeWidth={1.4}
                  className="text-[#0E7C8F] mt-0.5"
                />
                <div className="flex-1">
                  <p className="overline">Funnel email drip · 4 step</p>
                  <p className="text-sm font-light text-[#0B192C] mt-1">
                    Ogni iscritto riceve automaticamente 4 email di
                    follow-up nei <strong>12 giorni</strong> successivi
                    all&apos;iscrizione. Tick giornaliero alle{" "}
                    <strong>10:00</strong>.
                    {dripStatus.next_run && (
                      <>
                        {" "}
                        Prossimo invio:{" "}
                        <strong>
                          {new Date(dripStatus.next_run).toLocaleString(
                            "it-IT",
                            {
                              weekday: "long",
                              day: "2-digit",
                              month: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </strong>
                        .
                      </>
                    )}
                  </p>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    {[0, 1, 2, 3, 4].map((step) => (
                      <div
                        key={step}
                        data-testid={`drip-step-${step}`}
                        className="bg-[#FFFFFF] border border-[#0E7C8F]/20 px-3 py-2"
                      >
                        <p className="overline text-[10px]">
                          {step === 0 ? "Solo welcome" : `Step ${step}`}
                        </p>
                        <p className="font-serif-display text-2xl text-[#0B192C] leading-none mt-1">
                          {dripStatus.counts_by_step?.[step] ?? 0}
                        </p>
                        <p className="text-[10px] text-[#3A4F6B] mt-1 truncate">
                          {step === 0
                            ? "iscritti"
                            : dripStatus.schedule?.find((s) => s.step === step)
                                ?.subject || ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={runDripNow}
                disabled={dripRunning}
                data-testid="admin-drip-run-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0E7C8F] text-[#0E7C8F] text-xs uppercase tracking-[0.18em] hover:bg-[#0E7C8F] hover:text-[#F9F8F6] transition-colors disabled:opacity-60 whitespace-nowrap self-start"
              >
                {dripRunning ? (
                  <>
                    <Loader2 size={13} className="animate-spin" /> Tick...
                  </>
                ) : (
                  <>
                    <Send size={13} /> Esegui tick ora
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Email analytics strip */}
      {emailAnalytics && (
        <section
          data-testid="admin-email-analytics"
          className="border-b border-[#E5E1DA] bg-[#FFFFFF]"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6">
            <div className="flex items-baseline justify-between mb-4">
              <p className="overline">
                Email · ultimi {emailAnalytics.days} giorni
              </p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
                Tracking via Resend webhook
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
              <EmailStat label="Inviate" value={emailAnalytics.counts["email.sent"] || emailAnalytics.counts["email.delivered"]} />
              <EmailStat label="Recapitate" value={emailAnalytics.counts["email.delivered"]} accent="#0E7C8F" />
              <EmailStat label="Aperture" value={emailAnalytics.counts["email.opened"]} accent="#E89B6C" sub={`${emailAnalytics.open_rate}%`} />
              <EmailStat label="Click" value={emailAnalytics.counts["email.clicked"]} accent="#E89B6C" sub={`${emailAnalytics.click_rate}%`} />
              <EmailStat label="Bounce" value={emailAnalytics.counts["email.bounced"]} accent="#A82828" sub={`${emailAnalytics.bounce_rate}%`} />
              <EmailStat label="Spam" value={emailAnalytics.counts["email.complained"]} accent="#A82828" />
            </div>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="border-b border-[#E5E1DA] bg-[#FFFFFF] sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex overflow-x-auto -mb-px">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`inline-flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "border-[#E89B6C] text-[#0B192C]"
                      : "border-transparent text-[#3A4F6B] hover:text-[#0B192C]"
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {tab === "members" ? (
          <MembersTab onDelete={deleteSubscriber} />
        ) : tab === "courses" ? (
          <CoursesTab />
        ) : tab === "outreach" ? (
          <OutreachTab />
        ) : tab === "leads" ? (
          <LeadsTab />
        ) : tab === "analytics" ? (
          <AnalyticsTab />
        ) : tab === "bookstores" ? (
          <BookstoresTab token={getToken()} />
        ) : tab === "ledger" ? (
          <LedgerTab />
        ) : tab === "premium" ? (
          <PremiumCoursesTab />
        ) : loading ? (
          <div className="flex items-center gap-3 text-[#3A4F6B]">
            <Loader2 className="animate-spin" size={18} />
            <span className="text-sm">Caricamento...</span>
          </div>
        ) : error ? (
          <p data-testid="admin-content-error" className="text-sm text-red-700">
            {error}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[260px]">
                {tab !== "downloads" && (
                  <div className="relative flex-1 max-w-md">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A4F6B]"
                    />
                    <input
                      type="text"
                      placeholder="Cerca per email, nome..."
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      data-testid="admin-search-input"
                      className="w-full pl-9 pr-3 py-2 border border-[#0B192C]/20 bg-[#FFFFFF] text-sm focus:outline-none focus:border-[#0B192C]"
                    />
                  </div>
                )}
                <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B] whitespace-nowrap">
                  {data?.total ?? 0} record
                </p>
              </div>
              <div className="flex items-center gap-2">
                {tab === "subscribers" && (
                  <>
                    <button
                      onClick={() => setModal({ type: "add" })}
                      data-testid="admin-add-subscriber-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
                    >
                      <Plus size={13} /> Aggiungi
                    </button>
                    <button
                      onClick={syncAudience}
                      disabled={syncing}
                      data-testid="admin-sync-audience-btn"
                      title="Aggiunge gli iscritti all'Audience Resend"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0E7C8F] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] transition-colors disabled:opacity-60"
                    >
                      {syncing ? (
                        <>
                          <Loader2 size={13} className="animate-spin" /> Sync...
                        </>
                      ) : (
                        <>
                          <Send size={13} /> Sync Resend
                        </>
                      )}
                    </button>
                  </>
                )}
                {TABS.find((t) => t.id === tab)?.exportPath && (
                  <button
                    onClick={exportCsv}
                    data-testid={`admin-export-${tab}-btn`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
                  >
                    <FileDown size={13} />
                    CSV
                  </button>
                )}
              </div>
            </div>
            {tab === "subscribers" && syncResult && (
              <div
                data-testid="admin-sync-result"
                className="mb-6 border border-[#0E7C8F] bg-[#D6E5EC]/40 p-4 text-sm font-light text-[#0B192C]"
              >
                ✓ Sync completato · Audience{" "}
                <code className="font-mono text-xs">
                  {syncResult.audience_id?.slice(0, 8)}...
                </code>{" "}
                · <strong>{syncResult.total}</strong> iscritti processati
                {syncResult.failed > 0 && (
                  <span className="text-red-700">
                    {" "}
                    · {syncResult.failed} falliti
                  </span>
                )}
              </div>
            )}
            {tab === "subscribers" && (
              <Subscribers
                items={data?.items || []}
                onDelete={deleteSubscriber}
              />
            )}
            {tab === "messages" && (
              <Messages
                items={data?.items || []}
                onReply={(msg) =>
                  setModal({ type: "reply", payload: msg })
                }
              />
            )}
            {tab === "transactions" && <Transactions items={data?.items || []} />}
            {tab === "downloads" && <Downloads items={data?.items || []} />}
          </>
        )}
      </section>

      {modal && (
        <AdminModal
          modal={modal}
          onClose={() => setModal(null)}
          onSuccess={() => {
            setModal(null);
            loadTab(tab);
          }}
        />
      )}
      <ChatWidget persona="admin" authToken={getToken()} position="bottom-right" />
    </main>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#FFFFFF] p-6 md:p-8 flex items-start justify-between gap-4"
    >
      <div>
        <p className="overline">{label}</p>
        <p className="font-serif-display text-3xl md:text-4xl mt-2 text-[#0B192C]">
          {value}
        </p>
        {sublabel && (
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B] mt-2">
            {sublabel}
          </p>
        )}
      </div>
      <Icon size={20} strokeWidth={1.3} className="text-[#E89B6C] mt-1" />
    </div>
  );
}

function EmailStat({ label, value, sub, accent }) {
  return (
    <div className="bg-[#FFFFFF] p-4 md:p-5">
      <p className="overline text-[10px]">{label}</p>
      <p
        className="font-serif-display text-2xl md:text-3xl mt-1 leading-none"
        style={{ color: accent || "#0B192C" }}
      >
        {value ?? 0}
      </p>
      {sub && (
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B] mt-1">
          {sub}
        </p>
      )}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div
      data-testid="admin-empty-state"
      className="border border-dashed border-[#E5E1DA] py-16 text-center"
    >
      <p className="text-sm text-[#3A4F6B]">{children}</p>
    </div>
  );
}

function Subscribers({ items, onDelete }) {
  if (!items.length) return <EmptyState>Nessun iscritto.</EmptyState>;
  return (
    <div className="overflow-x-auto border border-[#E5E1DA]">
      <table className="w-full text-sm" data-testid="admin-subscribers-table">
        <thead className="bg-[#F4E8D6]/60 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
          <tr>
            <Th>Email</Th>
            <Th>Nome</Th>
            <Th>Data iscrizione</Th>
            <Th>Drip</Th>
            <Th>IP</Th>
            <Th>Azioni</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E5E1DA]">
          {items.map((r) => (
            <tr key={r.id} className="hover:bg-[#F9F8F6]">
              <Td mono>{r.email}</Td>
              <Td>{r.name || "—"}</Td>
              <Td>{fmtDate(r.created_at)}</Td>
              <Td>
                <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#0E7C8F]/10 text-[#0E7C8F]">
                  Step {r.drip_step ?? 0}
                </span>
              </Td>
              <Td mono className="text-[#3A4F6B] text-xs">
                {r.consent_ip || "—"}
              </Td>
              <Td>
                <button
                  onClick={() => onDelete(r.id)}
                  data-testid={`admin-delete-sub-${r.id}`}
                  title="Elimina iscritto"
                  className="text-red-700 hover:text-red-900"
                >
                  <Trash2 size={14} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Messages({ items, onReply }) {
  if (!items.length) return <EmptyState>Nessun messaggio.</EmptyState>;
  return (
    <div className="space-y-4" data-testid="admin-messages-list">
      {items.map((m) => (
        <div
          key={m.id}
          className="border border-[#E5E1DA] bg-[#FFFFFF] p-6"
          data-testid={`admin-message-${m.id}`}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <div>
              <p className="font-serif-display text-lg text-[#0B192C]">
                {m.name || "Anonimo"}
              </p>
              <a
                href={`mailto:${m.email}`}
                className="text-xs text-[#0E7C8F] link-underline break-all"
              >
                {m.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              {m.replied_at && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#0E7C8F]">
                  ✓ Risposto {fmtDate(m.replied_at)}
                </span>
              )}
              <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">
                {fmtDate(m.created_at)}
              </p>
              <button
                onClick={() => onReply(m)}
                data-testid={`admin-reply-${m.id}`}
                className="inline-flex items-center gap-1 px-3 py-1 border border-[#0E7C8F] text-[#0E7C8F] text-[10px] uppercase tracking-[0.18em] hover:bg-[#0E7C8F] hover:text-[#F9F8F6] transition-colors"
              >
                <Reply size={11} /> Rispondi
              </button>
            </div>
          </div>
          <p className="text-sm font-light leading-relaxed text-[#0B192C] whitespace-pre-wrap border-l-2 border-[#E89B6C] pl-4">
            {m.message}
          </p>
        </div>
      ))}
    </div>
  );
}

function Transactions({ items }) {
  if (!items.length) return <EmptyState>Nessuna transazione ancora.</EmptyState>;
  return (
    <div className="overflow-x-auto border border-[#E5E1DA]">
      <table className="w-full text-sm" data-testid="admin-transactions-table">
        <thead className="bg-[#F4E8D6]/60 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
          <tr>
            <Th>Pacchetto</Th>
            <Th>Importo</Th>
            <Th>Email</Th>
            <Th>Stato</Th>
            <Th>Data</Th>
            <Th>Sessione</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E5E1DA]">
          {items.map((t) => (
            <tr key={t.id} className="hover:bg-[#F9F8F6]">
              <Td>{t.package_id}</Td>
              <Td mono>
                € {Number(t.amount || 0).toFixed(2).replace(".", ",")}
              </Td>
              <Td mono className="text-xs">
                {t.email || "—"}
              </Td>
              <Td>
                <StatusBadge status={t.payment_status} />
              </Td>
              <Td>{fmtDate(t.created_at)}</Td>
              <Td mono className="text-[10px] text-[#3A4F6B] break-all">
                {t.session_id?.slice(-10) || "—"}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Downloads({ items }) {
  if (!items.length) return <EmptyState>Nessun download ancora.</EmptyState>;
  return (
    <div className="overflow-x-auto border border-[#E5E1DA]">
      <table className="w-full text-sm" data-testid="admin-downloads-table">
        <thead className="bg-[#F4E8D6]/60 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
          <tr>
            <Th>Asset</Th>
            <Th>Data download</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E5E1DA]">
          {items.map((d) => (
            <tr key={d.id} className="hover:bg-[#F9F8F6]">
              <Td mono className="text-xs">
                {d.asset}
              </Td>
              <Td>{fmtDate(d.downloaded_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { label: "Pagato", cls: "bg-[#0E7C8F] text-[#F9F8F6]" },
    pending: { label: "In attesa", cls: "bg-[#F4E8D6] text-[#0B192C]" },
    initiated: { label: "Avviata", cls: "bg-[#F4E8D6] text-[#0B192C]" },
    failed: { label: "Fallita", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-[#E5E1DA] text-[#0B192C]" };
  return (
    <span
      className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-normal">{children}</th>;
}

function Td({ children, mono, className = "" }) {
  return (
    <td
      className={`px-4 py-3 align-top ${
        mono ? "font-mono" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}
